import {
  useEffect,
  useRef,
  useState,
  type PointerEvent,
  type ReactNode,
} from "react";
import { Box, ButtonBase } from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import { animate, motion, useTransform, type MotionValue } from "framer-motion";
import { TABULAR, type TonalTokens } from "./tokens";

export interface ViewOption<K extends string> {
  key: K;
  label: string;
}

/** A horizontal drag this far commits the swipe; less springs back. */
export const VIEW_SWIPE_PX = 40;
const AXIS_LOCK_PX = 8;
// A drag from the left edge is the platform's back gesture (useEdgeSwipeBack
// in the apps, the browser's on mobile web), never a view swipe.
const EDGE_ZONE_PX = 24;

/** A view's place in the switch; an unknown key reads as the first. */
export const viewIndex = <K extends string>(
  views: readonly { key: K }[],
  view: K,
): number =>
  Math.max(
    views.findIndex((each) => each.key === view),
    0,
  );

/** Where the panes rest for a view's index, sprung or (reduced motion) at once. */
export const settleViews = (
  progress: MotionValue<number>,
  index: number,
  reduced: boolean,
) => {
  if (reduced) {
    progress.set(index);
    return undefined;
  }
  return animate(progress, index, {
    type: "spring",
    stiffness: 420,
    damping: 44,
  });
};

interface ViewSwitchProps<K extends string> {
  views: readonly ViewOption<K>[];
  view: K;
  onChange: (next: K) => void;
  /** The view's index; between two while a finger drags the panes. */
  progress: MotionValue<number>;
  /** A row of its own, the labels sharing its width. */
  fullWidth: boolean;
  tokens: TonalTokens;
  /** The group's accessible name, e.g. "Earnings view". */
  label: string;
}

/**
 * Plain labels with one soft green pill. The pill rides `progress`, so it
 * slides on a tap and follows the finger through a swipe of the panes.
 */
export const ViewSwitch = <K extends string>({
  views,
  view,
  onChange,
  progress,
  fullWidth,
  tokens,
  label,
}: ViewSwitchProps<K>) => {
  const pillX = useTransform(progress, (value) => `${value * 100}%`);
  return (
    <Box
      role="group"
      aria-label={label}
      data-testid="view-switch"
      sx={{
        position: "relative",
        display: "inline-grid",
        gridTemplateColumns: `repeat(${views.length}, 1fr)`,
        width: fullWidth ? "100%" : "auto",
      }}
    >
      <motion.span
        aria-hidden
        data-testid="view-pill"
        style={{
          x: pillX,
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: `${100 / Math.max(views.length, 1)}%`,
          borderRadius: 999,
          backgroundColor: alpha(tokens.gain, 0.12),
        }}
      />
      {views.map((each) => {
        const selected = each.key === view;
        return (
          <ButtonBase
            key={each.key}
            aria-pressed={selected}
            onClick={() => {
              if (!selected) onChange(each.key);
            }}
            sx={{
              ...TABULAR,
              position: "relative",
              height: 30,
              "@media (pointer: coarse)": { height: 36 },
              px: 1.75,
              borderRadius: "999px",
              fontSize: "0.8125rem",
              fontWeight: 500,
              letterSpacing: "0.01em",
              whiteSpace: "nowrap",
              color: selected ? tokens.gain : tokens.muted,
              transition: "color 150ms ease",
              "@media (hover: hover)": {
                "&:hover": { color: selected ? tokens.gain : tokens.text },
              },
              "&:focus-visible": {
                outline: `2px solid ${tokens.gain}`,
                outlineOffset: 2,
              },
            }}
          >
            {each.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
};

const enter = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: none; }
`;

export interface ViewPane<K extends string> {
  key: K;
  content: ReactNode;
}

interface ViewPanesProps<K extends string> {
  /** In the switch's order. */
  panes: readonly ViewPane<K>[];
  view: K;
  progress: MotionValue<number>;
  /** Touch: the views sit on one horizontal track and a drag moves between them. */
  swipeable: boolean;
  reduced: boolean;
  /** A drag past VIEW_SWIPE_PX let go: the view to go to. */
  onSwipe: (next: K) => void;
}

/**
 * The views. On touch they are panes on one horizontal track that follows
 * the finger (vertical drags still scroll the page; anything marked
 * `data-view-swipe="off"` keeps its own gesture). On pointer screens the
 * active view shows and the others wait, mounted once visited, so a switch
 * back never replays a load.
 */
export const ViewPanes = <K extends string>({
  panes,
  view,
  progress,
  swipeable,
  reduced,
  onSwipe,
}: ViewPanesProps<K>) => {
  const [visited, setVisited] = useState<Partial<Record<K, boolean>>>(
    () => ({ [view]: true }) as Partial<Record<K, boolean>>,
  );
  const [switched, setSwitched] = useState(false);
  const first = useRef(view);
  useEffect(() => {
    if (view !== first.current) setSwitched(true);
    setVisited((current) =>
      current[view] ? current : { ...current, [view]: true },
    );
  }, [view]);

  const index = viewIndex(panes, view);
  const last = panes.length - 1;
  const frame = useRef<HTMLDivElement | null>(null);
  const trackX = useTransform(progress, (value) => `${-value * 100}%`);
  const gesture = useRef<{
    id: number;
    x: number;
    y: number;
    width: number;
    axis: "x" | "y" | null;
  } | null>(null);
  const dragged = useRef(false);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    dragged.current = false;
    if (event.pointerType === "mouse" || event.clientX <= EDGE_ZONE_PX) return;
    const target = event.target as Element | null;
    if (target?.closest?.('[data-view-swipe="off"]')) return;
    gesture.current = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      width: Math.max(frame.current?.clientWidth ?? 0, 1),
      axis: null,
    };
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const current = gesture.current;
    if (current === null || current.id !== event.pointerId) return;
    const dx = event.clientX - current.x;
    const dy = event.clientY - current.y;
    if (current.axis === null) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) < AXIS_LOCK_PX) return;
      current.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      // Touch is captured implicitly; a pen is not, and its release outside
      // the frame would leave the panes mid-way.
      if (current.axis === "x")
        event.currentTarget.setPointerCapture?.(event.pointerId);
    }
    if (current.axis !== "x") return;
    dragged.current = true;
    const next = index - dx / current.width;
    progress.set(Math.min(Math.max(next, 0), last));
  };
  const release = (event: PointerEvent<HTMLDivElement>, cancelled: boolean) => {
    const current = gesture.current;
    if (current === null || current.id !== event.pointerId) return;
    gesture.current = null;
    if (current.axis !== "x") return;
    const dx = event.clientX - current.x;
    let towards: number | null = null;
    if (dx <= -VIEW_SWIPE_PX) towards = index + 1;
    if (dx >= VIEW_SWIPE_PX) towards = index - 1;
    if (!cancelled && towards !== null && towards >= 0 && towards <= last) {
      onSwipe(panes[towards].key);
      return;
    }
    settleViews(progress, index, reduced);
  };

  if (!swipeable)
    return (
      <Box data-testid="views" data-view={view}>
        {panes.map(
          (pane) =>
            visited[pane.key] && (
              <Box
                key={pane.key}
                data-testid={`view-pane-${pane.key}`}
                hidden={view !== pane.key}
                sx={{
                  animation:
                    switched && !reduced ? `${enter} 180ms ease-out` : "none",
                }}
              >
                {pane.content}
              </Box>
            ),
        )}
      </Box>
    );

  return (
    <Box
      ref={frame}
      data-testid="views"
      data-view={view}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={(event) => release(event, false)}
      onPointerCancel={(event) => release(event, true)}
      onClickCapture={(event) => {
        if (!dragged.current) return;
        dragged.current = false;
        event.stopPropagation();
        event.preventDefault();
      }}
      sx={{ overflow: "hidden", touchAction: "pan-y" }}
    >
      <motion.div
        data-testid="view-track"
        style={{ x: trackX, position: "relative" }}
      >
        {panes.map((pane, at) => {
          const active = pane.key === view;
          return (
            <Box
              key={pane.key}
              data-testid={`view-pane-${pane.key}`}
              aria-hidden={active ? undefined : true}
              inert={!active}
              sx={{
                width: "100%",
                top: 0,
                // The active pane is in flow, so the page is its height; the
                // others ride beside it on the track.
                position: active ? "relative" : "absolute",
                left: `${at * 100}%`,
              }}
            >
              {pane.content}
            </Box>
          );
        })}
      </motion.div>
    </Box>
  );
};
